/**
 * Frontend parser for OMR scanner raw exam files.
 * Mirrors the backend parse_raw_exam_file.py logic for client-side validation.
 */

// --- Helpers ---
function isBlank(v) {
	return String(v).trim() === "";
}

function getRawAnswerStringWidth(lowestQuestion, highestQuestion) {
	const lq = Number.parseInt(lowestQuestion, 10) || 0;
	const hq = Number.parseInt(highestQuestion, 10) || 0;
	return Math.max(0, hq, hq - lq + 1);
}

function getAnswerCharForQuestion(rawAnswers, questionNo, lowestQuestion, highestQuestion) {
	const text = String(rawAnswers || "");
	if (!text) return "";

	const q = Number.parseInt(questionNo, 10);
	const lq = Number.parseInt(lowestQuestion, 10);
	const hq = Number.parseInt(highestQuestion, 10);
	const idx = Number.isFinite(hq) && hq > 0 && text.length >= hq ? q - 1 : q - lq;

	return idx >= 0 && idx < text.length ? text[idx] || "" : "";
}

function buildAnswerKeyDict(rawAnswers, lowestQuestion, highestQuestion) {
	const answerKeyDict = {};
	const lq = Number.parseInt(lowestQuestion, 10) || 0;
	const hq = Number.parseInt(highestQuestion, 10) || 0;

	for (let q = lq; q <= hq; q++) {
		answerKeyDict[q] = getAnswerCharForQuestion(rawAnswers, q, lq, hq);
	}

	return answerKeyDict;
}

// --- Main parsing functions ---

/**
 * Process the header line to extract exam parameters.
 * @param {string} headerLine - The first (header) line of the raw file
 * @returns {Object} Parsed header object with all exam specification fields
 */
export function processHeader(headerLine) {
	// Store the original line for reconstruction
	const originalHeaderLine = headerLine;

	// Split by space but keep track of the raw parts (including empty strings for spacing)
	const rawParts = headerLine.split(" ");
	const headers = rawParts.map((h) => h.trim()).filter((h) => h !== "");

	if (headers.length < 11) {
		return {
			error: true,
			message: `Header line appears to be incomplete. Expected at least 11 space-separated fields, got ${headers.length}.`,
			rawHeaders: headers,
			rawParts,
			originalHeaderLine,
		};
	}

	const initLine = headers[0];

	let sheetType = "unknown";
	if (initLine.substring(0, 3) === "506") {
		sheetType = "blue";
	} else if (initLine.substring(0, 3) === "500") {
		sheetType = "green";
	}

	const sheetSequence = initLine.substring(3, 9);

	const reqnum = headers[4];
	const lqnum = headers[9];
	const formatString = headers[10] || "";

	// Handle case where format string is missing or too short
	if (formatString.length < 19) {
		// Return partial parse with what we have, marking fields as missing
		return {
			error: true,
			message: `Format string is too short or missing. Expected at least 20 characters, got ${formatString.length}. Some fields may need manual entry.`,
			rawFormatString: formatString,
			// Provide defaults for missing fields so user can edit them
			reqnum,
			lqnum: lqnum || "001",
			hqnum: formatString.substring(0, 3) || "050",
			qlen: formatString.substring(3, 5) || "05",
			nkeys: formatString.substring(5, 6) || "1",
			a: formatString[6] || "0",
			b: formatString[7] || "0",
			c: formatString[8] || "0",
			d: formatString[9] || "0",
			e: formatString[10] || "0",
			f: formatString[11] || "0",
			k: formatString[12] || "0",
			l: formatString[13] || "0",
			m: formatString[14] || "0",
			n: formatString[15] || "0",
			o: formatString[16] || "0",
			p: formatString[17] || "0",
			initials: formatString.substring(18, 20) || "XX",
			program: headers[8] || "",
			coursetitle: headers.slice(11).join(" ") || "",
			sheet_type: sheetType,
			sheet_sequence: sheetSequence,
			weighted: false,
			rawHeaders: headers,
			rawParts,
			originalHeaderLine,
		};
	}

	const hqnum = formatString.substring(0, 3);
	const qlen = formatString.substring(3, 5);
	const nkeys = formatString.substring(5, 6);

	// Extract individual flag characters
	const a = formatString[6];
	const b = formatString[7];
	const c = formatString[8];
	const d = formatString[9];
	const e = formatString[10];
	const f = formatString[11];
	const k = formatString[12];
	const l = formatString[13];
	const m = formatString[14];
	const n = formatString[15];
	const o = formatString[16];
	const p = formatString[17];
	const initials = formatString.substring(18, 20);

	const weighted = parseInt(e, 10) > 0;

	const program = headers[8];

	let coursetitle = headers.slice(11).join(" ");
	if (coursetitle.startsWith(":") && coursetitle.endsWith("<")) {
		coursetitle = coursetitle.substring(1, coursetitle.length - 1);
	}

	return {
		reqnum,
		lqnum,
		hqnum,
		qlen,
		nkeys,
		a,
		b,
		c,
		d,
		e,
		f,
		k,
		l,
		m,
		n,
		o,
		p,
		initials,
		program,
		coursetitle,
		sheet_type: sheetType,
		sheet_sequence: sheetSequence,
		weighted,
		error: false,
		// Store raw data for reconstruction
		rawHeaders: headers,
		rawParts,
		originalHeaderLine,
	};
}

/**
 * Process a non-header line (answer key or student response).
 * @param {string} line - A single line from the file
 * @param {string} sheetType - "green" or "blue"
 * @param {number|string} nkeys - Number of answer key versions
 * @param {number} numberOfQuestions - Total number of questions
 * @param {Array} answerKeys - Array of already-parsed answer keys (for score mapping)
 * @param {boolean} weightedExam - Whether this is a weighted exam
 * @param {number|string} eValue - The 'e' value from header (for weighted exams)
 * @returns {Object} Parsed line object
 */
export function processNonHeaderLine(
	line,
	sheetType,
	nkeys,
	numberOfQuestions,
	answerKeys,
	weightedExam,
	eValue,
	lowestQuestion,
	highestQuestion,
	options = {},
) {
	const { includeAnswerKeyDicts = true, includeScoresForEachKeyVersion = true } = options;
	// Remove trailing '<' if present
	if (line && line[line.length - 1] === "<") {
		line = line.substring(0, line.length - 1);
	}

	if (sheetType === "green") {
		return processGreenSheetLine(
			line,
			nkeys,
			numberOfQuestions,
			answerKeys,
			eValue,
			lowestQuestion,
			highestQuestion,
			includeAnswerKeyDicts,
			includeScoresForEachKeyVersion,
		);
	} else if (sheetType === "blue") {
		return processBlueSheetLine(
			line,
			nkeys,
			numberOfQuestions,
			answerKeys,
			weightedExam,
			eValue,
			lowestQuestion,
			highestQuestion,
			includeAnswerKeyDicts,
			includeScoresForEachKeyVersion,
		);
	} else {
		return {
			error: true,
			message: `Unknown sheet type: ${sheetType}`,
		};
	}
}

function processGreenSheetLine(
	line,
	nkeys,
	numberOfQuestions,
	answerKeys,
	eValue,
	lowestQuestion,
	highestQuestion,
	includeAnswerKeyDicts = true,
	includeScoresForEachKeyVersion = true,
) {
	const sheetSequence = line.substring(5, 9);
	const init = line.substring(0, 21);
	const uselessInfo = line.substring(21, 40);
	const answerKeyIdent = line.substring(40, 41);
	let name = line.substring(42, 63);
	if (name.trim() === "") {
		name = "(missing) (missing)";
	}
	// const sex = line.substring(63, 64);
	// const grade = line.substring(64, 65);
	// const birthdate = line.substring(65, 71);
	const idNumber = line.substring(72, 79);

	if (String(nkeys) === "1") {
		const originalScore = parseInt(line.substring(83, 86), 10);
		const rawAnswerWidth = getRawAnswerStringWidth(lowestQuestion, highestQuestion);
		const answersChosen = line.substring(87, 87 + rawAnswerWidth);
		const result = {
			sheet_sequence: sheetSequence,
			init,
			useless_info: uselessInfo,
			answer_key_ident: answerKeyIdent,
			name: answerKeyIdent === "K" ? "Key" : name.trim(),
			id_number: idNumber,
			special_code: null,
			original_score: originalScore,
			answers_chosen: answersChosen,
		};
		if (includeAnswerKeyDicts) {
			result.answer_key_dict = buildAnswerKeyDict(
				answersChosen,
				lowestQuestion,
				highestQuestion,
			);
		}
		return result;
	} else {
		const specialCode = line.substring(83, 89);
		const numberCharsAfterSpecialCode = 4 * parseInt(nkeys, 10);
		const rawScores = line.substring(89, 89 + numberCharsAfterSpecialCode);
		const chunks = [];
		for (let i = 0; i < rawScores.length; i += 4) {
			chunks.push(rawScores.substring(i, i + 4));
		}

		const scoresForEachKey = {};
		chunks.forEach((chunk, idx) => {
			if (chunk.trim()) {
				scoresForEachKey[idx + 1] = parseInt(chunk.trim(), 10);
			}
		});

		const scoresForEachKeyVersion = includeScoresForEachKeyVersion ? {} : undefined;
		if (includeScoresForEachKeyVersion) {
			answerKeys.forEach((key) => {
				const specialCodeVal = key.data.special_code;
				const answerKeyIndex = key.index;
				scoresForEachKeyVersion[specialCodeVal] = scoresForEachKey[answerKeyIndex] ?? "N/A";
			});
		}

		const answersChosen = line.substring(
			90 + numberCharsAfterSpecialCode,
			90 + numberCharsAfterSpecialCode + getRawAnswerStringWidth(lowestQuestion, highestQuestion),
		);
		const result = {
			sheet_sequence: sheetSequence,
			init,
			useless_info: uselessInfo,
			answer_key_ident: answerKeyIdent,
			name: answerKeyIdent === "K" ? `Key ${specialCode}` : name.trim(),
			id_number: idNumber,
			special_code: specialCode,
			scores_for_each_key: scoresForEachKey,
			answers_chosen: answersChosen,
		};
		if (includeScoresForEachKeyVersion) {
			result.scores_for_each_key_version = scoresForEachKeyVersion;
		}
		if (includeAnswerKeyDicts) {
			result.answer_key_dict = buildAnswerKeyDict(
				answersChosen,
				lowestQuestion,
				highestQuestion,
			);
		}
		return result;
	}
}

function processBlueSheetLine(
	line,
	nkeys,
	numberOfQuestions,
	answerKeys,
	weightedExam,
	eValue,
	lowestQuestion,
	highestQuestion,
	includeAnswerKeyDicts = true,
	includeScoresForEachKeyVersion = true,
) {
	const sheetSequence = line.substring(5, 9);
	const init = line.substring(0, 21);
	const uselessInfo = line.substring(21, 40);
	const answerKeyIdent = line.substring(40, 41);
	let name = line.substring(42, 62);
	if (name.trim() === "") {
		name = "(missing) (missing)";
	}
	const idNumber = line.substring(63, 70);

	if (String(nkeys) === "1") {
		const originalScore = parseInt(line.substring(73, 76), 10);
		const rawAnswerWidth = getRawAnswerStringWidth(lowestQuestion, highestQuestion);
		const answersChosen = line.substring(77, 77 + rawAnswerWidth);
		const result = {
			sheet_sequence: sheetSequence,
			init,
			useless_info: uselessInfo,
			answer_key_ident: answerKeyIdent,
			name: answerKeyIdent === "K" ? "Key" : name.trim(),
			id_number: idNumber,
			special_code: null,
			original_score: originalScore,
			answers_chosen: answersChosen,
		};
		if (includeAnswerKeyDicts) {
			result.answer_key_dict = buildAnswerKeyDict(
				answersChosen,
				lowestQuestion,
				highestQuestion,
			);
		}
		return result;
	} else {
		let specialCode;
		let numberCharsAfterSpecialCode;

		if (weightedExam) {
			numberCharsAfterSpecialCode = 5 * parseInt(eValue, 10);
			specialCode = line.substring(73, 79).trim() || null;
		} else {
			numberCharsAfterSpecialCode = 4 * parseInt(nkeys, 10);
			specialCode = line.substring(73, 79);
		}

		const rawScores = line.substring(87, 87 + numberCharsAfterSpecialCode);
		const chunks = [];
		for (let i = 0; i < rawScores.length; i += 4) {
			chunks.push(rawScores.substring(i, i + 4));
		}

		const scoresForEachKey = {};
		chunks.forEach((chunk, idx) => {
			if (chunk.trim()) {
				scoresForEachKey[idx + 1] = parseInt(chunk.trim(), 10);
			}
		});

		const scoresForEachKeyVersion = includeScoresForEachKeyVersion ? {} : undefined;
		if (includeScoresForEachKeyVersion) {
			answerKeys.forEach((key) => {
				const specialCodeVal = key.data.special_code;
				const answerKeyIndex = key.index;
				scoresForEachKeyVersion[specialCodeVal] = scoresForEachKey[answerKeyIndex] ?? "N/A";
			});
		}

		const answersChosen = line.substring(
			88 + numberCharsAfterSpecialCode,
			88 + numberCharsAfterSpecialCode + getRawAnswerStringWidth(lowestQuestion, highestQuestion),
		);
		const result = {
			sheet_sequence: sheetSequence,
			init,
			useless_info: uselessInfo,
			answer_key_ident: answerKeyIdent,
			name: answerKeyIdent === "K" ? `Key ${specialCode}` : name.trim(),
			id_number: idNumber,
			special_code: specialCode,
			scores_for_each_key: scoresForEachKey,
			answers_chosen: answersChosen,
		};
		if (includeScoresForEachKeyVersion) {
			result.scores_for_each_key_version = scoresForEachKeyVersion;
		}
		if (includeAnswerKeyDicts) {
			result.answer_key_dict = buildAnswerKeyDict(
				answersChosen,
				lowestQuestion,
				highestQuestion,
			);
		}
		return result;
	}
}

/**
 * Assign scores to students based on their matching answer key version.
 * @param {Array} studentResponses - Parsed student response objects
 * @param {Array} answerKeys - Parsed answer key objects
 * @returns {Array} Updated student responses with scores assigned
 */
function assignScoreToStudentMultipleKeys(studentResponses, answerKeys) {
	if (answerKeys.length <= 1) {
		return studentResponses;
	}

	for (const student of studentResponses) {
		let studentCodeMatched = false;

		if (student.special_code && student.special_code.trim()) {
			// Try to match by special code
			for (const key of answerKeys) {
				if (key.data.special_code === student.special_code) {
					student.original_score = student.scores_for_each_key?.[key.index] ?? null;
					student.version_guessed = false;
					studentCodeMatched = true;
					break;
				}
			}

			if (!studentCodeMatched) {
				// Guess best key based on highest score
				const bestIndex = Object.entries(student.scores_for_each_key || {}).reduce(
					(best, [idx, score]) => {
						if (best === null || score > (student.scores_for_each_key[best] ?? -Infinity)) {
							return parseInt(idx, 10);
						}
						return best;
					},
					null,
				);

				if (bestIndex !== null) {
					student.original_score = student.scores_for_each_key[bestIndex] ?? null;
					for (const key of answerKeys) {
						if (key.index === bestIndex) {
							student.original_special_code = student.special_code;
							student.special_code = key.data.special_code;
							student.version_guessed = true;
							studentCodeMatched = true;
							break;
						}
					}
				}
			}
		} else {
			// No special code - guess best key based on highest score
			const bestIndex = Object.entries(student.scores_for_each_key || {}).reduce(
				(best, [idx, score]) => {
					if (best === null || score > (student.scores_for_each_key[best] ?? -Infinity)) {
						return parseInt(idx, 10);
					}
					return best;
				},
				null,
			);

			if (bestIndex !== null) {
				student.original_score = student.scores_for_each_key[bestIndex] ?? null;
				for (const key of answerKeys) {
					if (key.index === bestIndex) {
						student.original_special_code = student.special_code;
						student.special_code = key.data.special_code;
						student.version_guessed = true;
						studentCodeMatched = true;
						break;
					}
				}
			}
		}
	}

	return studentResponses;
}

/**
 * Main parsing function for a raw exam file.
 * @param {string} content - The raw file content as a string
 * @returns {Object} { header, answerKeys, studentResponses, errors, warnings }
 */
export function parseFile(content, options = {}) {
	const {
		includeAnswerKeyDicts = true,
		includeWeightedKeyMaps = true,
		assignScores = true,
		includeScoresForEachKeyVersion = true,
	} = options;
	const lines = content.split(/\r?\n/);
	const errors = [];
	const warnings = [];

	let headerObj = {};
	const parsedAnswerKeyArray = [];
	const parsedStudentResponseArray = [];

	let lineIndex = 0;
	for (const line of lines) {
		if (!line.trim()) {
			lineIndex++;
			continue; // Skip empty lines
		}

		if (lineIndex === 0) {
			// First non-empty line is the header
			headerObj = processHeader(line);
			if (headerObj.error) {
				errors.push({
					line: lineIndex + 1,
					type: "header",
					message: headerObj.message,
					raw: line,
				});
			}
			lineIndex++;
			continue;
		}

		const nkeys = headerObj.nkeys || 0;
		const sheetType = headerObj.sheet_type || "unknown";
		const highestQuestion = parseInt(headerObj.hqnum, 10) || 0;
		const lowestQuestion = parseInt(headerObj.lqnum, 10) || 0;
		const weightedExam = headerObj.weighted || false;
		const numberOfQuestions = highestQuestion - lowestQuestion + 1;
		const eValue = headerObj.e || 0;

		try {
			const processed = processNonHeaderLine(
				line,
				sheetType,
				nkeys,
				numberOfQuestions,
				parsedAnswerKeyArray,
				weightedExam,
				eValue,
				lowestQuestion,
				highestQuestion,
				{
					includeAnswerKeyDicts,
					includeScoresForEachKeyVersion,
				},
			);

			if (processed.error) {
				errors.push({
					line: lineIndex + 1,
					type: "line_parse",
					message: processed.message,
					raw: line,
				});
			} else if (lineIndex < parseInt(nkeys, 10) + 1) {
				// This is an answer key line
				parsedAnswerKeyArray.push({
					index: lineIndex,
					name: processed.name,
					data: processed,
				});
			} else {
				// This is a student response line
				parsedStudentResponseArray.push(processed);
			}
		} catch (err) {
			errors.push({
				line: lineIndex + 1,
				type: "exception",
				message: err.message,
				raw: line.substring(0, 100) + (line.length > 100 ? "..." : ""),
			});
		}

		lineIndex++;
	}

	// Validate minimum requirements
	if (!headerObj || headerObj.error) {
		errors.push({
			line: 1,
			type: "validation",
			message: "No valid header found in file.",
		});
	}

	if (parsedAnswerKeyArray.length === 0) {
		errors.push({
			type: "validation",
			message: "No answer keys found in file.",
		});
	}

	if (parsedStudentResponseArray.length === 0) {
		warnings.push({
			type: "validation",
			message: "No student responses found in file.",
		});
	}

	// Check if e value indicates a weighted exam
	const eVal = parseInt(headerObj.e, 10) || 0;
	const isWeightedExam = eVal > 0;

	for (const key of parsedAnswerKeyArray) {
		key.isWeightedExam = isWeightedExam;
	}

	if (includeWeightedKeyMaps && isWeightedExam) {
		// Build combined answer key dict for weighted exams
		const combinedAnswerKeyDict = {};
		const lowestQuestion = parseInt(headerObj.lqnum, 10) || 0;
		const highestQuestion = parseInt(headerObj.hqnum, 10) || 0;
		for (let q = lowestQuestion; q <= highestQuestion; q++) {
			combinedAnswerKeyDict[q] = "";
		}

		for (const key of parsedAnswerKeyArray) {
			const answers = key.data.answers_chosen || "";
			for (let q = lowestQuestion; q <= highestQuestion; q++) {
				const v = getAnswerCharForQuestion(answers, q, lowestQuestion, highestQuestion);
				if (!isBlank(v) && isBlank(combinedAnswerKeyDict[q])) {
					combinedAnswerKeyDict[q] = v.toUpperCase();
				}
			}
		}

		for (const key of parsedAnswerKeyArray) {
			key.combined_answer_key_dict = combinedAnswerKeyDict;
		}
	} else if (includeAnswerKeyDicts) {
		// Non-weighted exam processing
		for (const key of parsedAnswerKeyArray) {
			key.answer_key_dict = buildAnswerKeyDict(
				key.data.answers_chosen || "",
				headerObj.lqnum,
				headerObj.hqnum,
			);
		}
	}

	// Assign scores to students for multi-key exams
	const updatedStudentResponses = assignScores
		? assignScoreToStudentMultipleKeys(parsedStudentResponseArray, parsedAnswerKeyArray)
		: parsedStudentResponseArray;

	return {
		header: headerObj,
		answerKeys: parsedAnswerKeyArray,
		studentResponses: updatedStudentResponses,
		errors,
		warnings,
		isValid: errors.length === 0,
	};
}

/**
 * Parse just the header from file content.
 * @param {string} content - The raw file content as a string
 * @returns {Object} Parsed header object
 */
export function parseFileHeader(content) {
	const lines = content.split(/\r?\n/);
	for (const line of lines) {
		if (line.trim()) {
			return processHeader(line);
		}
	}
	return { error: true, message: "No header line found in file" };
}

/**
 * Validate header values and return validation issues.
 * Validates the actual field values, allowing user edits to fix parse errors.
 * @param {Object} header - Parsed header object (may be user-edited)
 * @returns {Array} Array of validation issues with field, message, and severity
 */
export function validateHeader(header) {
	const issues = [];

	// Don't short-circuit on header.error - validate actual values so user edits can fix issues
	// If there was a parse error message, show it as informational but continue validating
	if (header.error && header.message) {
		issues.push({
			field: "parse_warning",
			message: `Original parse issue: ${header.message} - Please verify all values below are correct.`,
			severity: "warning",
		});
	}

	// Check reqnum
	if (!header.reqnum || !/^\d+$/.test(header.reqnum)) {
		issues.push({
			field: "reqnum",
			value: header.reqnum,
			message: "Request number should be numeric.",
			severity: "error",
		});
	}

	// Check question range
	const lqnum = parseInt(header.lqnum, 10);
	const hqnum = parseInt(header.hqnum, 10);
	if (isNaN(lqnum) || isNaN(hqnum)) {
		issues.push({
			field: "question_range",
			value: `${header.lqnum}-${header.hqnum}`,
			message: "Question numbers should be numeric.",
			severity: "error",
		});
	} else if (hqnum < lqnum) {
		issues.push({
			field: "question_range",
			value: `${lqnum}-${hqnum}`,
			message: "High question number should be >= low question number.",
			severity: "error",
		});
	} else if (hqnum - lqnum > 300) {
		issues.push({
			field: "question_range",
			value: `${lqnum}-${hqnum}`,
			message: "Question range seems unusually large (>300 questions).",
			severity: "warning",
		});
	}

	// Check nkeys
	const nkeys = parseInt(header.nkeys, 10);
	if (isNaN(nkeys) || nkeys < 1) {
		issues.push({
			field: "nkeys",
			value: header.nkeys,
			message: "Number of keys must be at least 1.",
			severity: "error",
		});
	} else if (nkeys > 10) {
		issues.push({
			field: "nkeys",
			value: header.nkeys,
			message: "Number of keys seems unusually high (>10).",
			severity: "warning",
		});
	}

	// Check sheet type
	if (header.sheet_type === "unknown") {
		issues.push({
			field: "sheet_type",
			value: header.sheet_type,
			message: 'Could not determine sheet type (expected "green" or "blue").',
			severity: "warning",
		});
	}

	return issues;
}

/**
 * Reconstruct the format string (headers[10]) from edited header values.
 * Format: hqnum(3) + qlen(2) + nkeys(1) + a-p(12) + initials(2) = 20 chars
 * @param {Object} header - The edited header object
 * @returns {string} The reconstructed format string
 */
export function reconstructFormatString(header) {
	// Pad/trim each field to its expected length
	const hqnum = String(header.hqnum || "050")
		.padStart(3, "0")
		.substring(0, 3);
	const qlen = String(header.qlen || "05")
		.padStart(2, "0")
		.substring(0, 2);
	const nkeys = String(header.nkeys || "1").substring(0, 1);

	// Spec flags (a-p) - each is 1 character
	const a = String(header.a || "0").substring(0, 1);
	const b = String(header.b || "0").substring(0, 1);
	const c = String(header.c || "0").substring(0, 1);
	const d = String(header.d || "0").substring(0, 1);
	const e = String(header.e || "0").substring(0, 1);
	const f = String(header.f || "0").substring(0, 1);
	const k = String(header.k || "0").substring(0, 1);
	const l = String(header.l || "0").substring(0, 1);
	const m = String(header.m || "0").substring(0, 1);
	const n = String(header.n || "0").substring(0, 1);
	const o = String(header.o || "0").substring(0, 1);
	const p = String(header.p || "0").substring(0, 1);

	const initials = String(header.initials || "XX")
		.padEnd(2, "X")
		.substring(0, 2);

	return hqnum + qlen + nkeys + a + b + c + d + e + f + k + l + m + n + o + p + initials;
}

/**
 * Reconstruct the header line from edited header values.
 * The header line format (space-separated):
 *   [0] initLine, [1-3] other fields, [4] reqnum, [5-7] other, [8] program,
 *   [9] lqnum, [10] formatString, [11+] coursetitle
 * @param {Object} editedHeader - The edited header object
 * @param {Object} originalHeader - The original parsed header (for rawHeaders)
 * @returns {string} The reconstructed header line
 */
export function reconstructHeaderLine(editedHeader, originalHeader) {
	// Get the raw headers from original parse
	const rawHeaders = originalHeader?.rawHeaders || [];

	if (rawHeaders.length < 11) {
		// Can't reconstruct - not enough original data
		// Build a minimal valid header
		console.warn("Insufficient raw header data for reconstruction");
		return null;
	}

	// Clone the raw headers array
	const newHeaders = [...rawHeaders];

	// [9] = lqnum (editable in the dialog)
	if (editedHeader.lqnum !== undefined) {
		newHeaders[9] = String(editedHeader.lqnum);
	}

	// [10] = format string (hqnum + qlen + nkeys + a-p flags + initials)
	newHeaders[10] = reconstructFormatString(editedHeader);

	// Join with space (the original delimiter)
	return newHeaders.join(" ");
}

/**
 * Generate a corrected file content from the parsed data.
 * Replaces the header line with reconstructed values while keeping all other lines intact.
 * @param {Object} editedHeader - The (potentially edited) header object
 * @param {Object} originalHeader - The original parsed header (for rawHeaders)
 * @param {string} originalContent - The original file content
 * @returns {string} The reconstructed file content with corrected header
 */
export function generateCorrectedFile(editedHeader, originalHeader, originalContent) {
	const lines = originalContent.split(/\r?\n/);

	// Find the first non-empty line (the header)
	let headerLineIndex = -1;
	for (let i = 0; i < lines.length; i++) {
		if (lines[i].trim()) {
			headerLineIndex = i;
			break;
		}
	}

	if (headerLineIndex === -1) {
		// No header found, return original
		return originalContent;
	}

	// Reconstruct the header line
	const newHeaderLine = reconstructHeaderLine(editedHeader, originalHeader);

	if (newHeaderLine) {
		lines[headerLineIndex] = newHeaderLine;
	}

	// Preserve original line endings
	const lineEnding = originalContent.includes("\r\n") ? "\r\n" : "\n";
	return lines.join(lineEnding);
}

/**
 * Create a File object with corrected content for upload.
 * @param {Object} editedHeader - The edited header values
 * @param {Object} originalHeader - The original parsed header
 * @param {string} originalContent - The original file content
 * @param {string} originalFileName - The original file name
 * @returns {File} A new File object with corrected content
 */
export function createCorrectedFile(
	editedHeader,
	originalHeader,
	originalContent,
	originalFileName,
) {
	const correctedContent = generateCorrectedFile(editedHeader, originalHeader, originalContent);
	const blob = new Blob([correctedContent], { type: "text/plain" });
	return new File([blob], originalFileName || "corrected_exam_file.txt", { type: "text/plain" });
}

const parseRawExamFileUtils = {
	parseFile,
	parseFileHeader,
	processHeader,
	validateHeader,
	generateCorrectedFile,
	reconstructHeaderLine,
	reconstructFormatString,
	createCorrectedFile,
};

export default parseRawExamFileUtils;
